import React from 'react';
import { Skeleton, Text } from '@/components/ui';

interface MessageCellProps {
  value?: unknown;
  type?: 'error' | 'warning' | 'success' | 'info' | 'default';
  truncate?: boolean;
  maxWidth?: string;
  render?: () => React.ReactNode;
}

// Determine message type from content
const detectMessageType = (message: string): 'error' | 'warning' | 'success' | 'info' | 'default' => {
  const lower = message.toLowerCase();
  
  if (
    lower.includes('error') ||
    lower.includes('failed') ||
    lower.includes('invalid') ||
    lower.includes('not allowed')
  ) {
    return 'error';
  }
  
  if (
    lower.includes('warning') ||
    lower.includes('duplicate') ||
    lower.includes('caution')
  ) {
    return 'warning';
  }
  
  if (
    lower.includes('success') ||
    lower.includes('completed') ||
    lower.includes('valid')
  ) {
    return 'success';
  }
  
  if (
    lower.includes('info') ||
    lower.includes('note') ||
    lower.includes('information')
  ) {
    return 'info';
  }
  
  return 'default';
};

function MessageCell({
  value,
  type,
  truncate = true,
  maxWidth = '200px',
  render,
}: MessageCellProps) {
  if (render) {
    return <>{render()}</>;
  }

  if (value === null || value === undefined || value === '') {
    return null;
  }

  const message = String(value);
  const messageType = type || detectMessageType(message);

  const colorMap: Record<string, React.ComponentProps<typeof Text>['color']> = {
    error: 'error',
    warning: 'warning',
    success: 'success',
    info: 'info',
    default: 'textSecondary',
  };

  const color = colorMap[messageType] || 'textSecondary';

  return (
    <Text
      size="3xsmall"
      color={color}
      truncate={truncate}
      style={{
        maxWidth: truncate ? maxWidth : undefined,
        overflow: truncate ? 'hidden' : undefined,
        textOverflow: truncate ? 'ellipsis' : undefined,
        whiteSpace: truncate ? 'nowrap' : undefined,
      }}
      className="data-cell__message"
    >
      {message}
    </Text>
  );
}

export function MessageCellSkeleton() {
  return (
    <Skeleton style={{ width: '150px', height: '16px', borderRadius: '4px' }} />
  );
}

export default MessageCell;

